import { Card } from "@prisma/ui";
import { useQuery } from "@tanstack/react-query";
import { Image, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { HonestState } from "../../components/HonestState";
import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";

type Row = components["schemas"]["AwaitingItemOut"];

/** Image Studio and Video Studio in M1 (locked decisions 5 and 10): the honest state, and the
 *  approved plan items waiting for the AI Production Phase. No prompt, output, queue or credits. */
export function StudioPage({ studio }: { studio: "image" | "video" }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const q = useQuery({ queryKey: ["studio", studio],
    queryFn: () => call<Row[]>(api.GET("/app/v1/studio/{studio}/awaiting", { params: { path: { studio } } })) });
  return (
    <div className="space-y-8">
      <PageHeader titleKey={`nav.${studio}_studio`} />
      <HonestState icon={studio === "image" ? Image : Video} kind="ai_production_phase" descriptionKey={`honest.${studio}_studio`} />
      <section aria-labelledby="awaiting-title">
        <h2 id="awaiting-title" className="mb-3 text-[15px] font-semibold uppercase tracking-[0.06em] text-violet-text">{t("studio.waiting_here")}</h2>
        <QueryState query={q} emptyKey="studio.none_waiting">
          {(rows) => (
            <Card className="divide-y divide-[var(--border-default)]">
              {rows.map((r, n) => (
                <Link key={`${r.display_id}-${n}`} to={`/projects/${r.display_id}/plan`} className="flex flex-wrap items-center gap-4 px-5 py-4 hover:bg-elevated">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-ink">{r.product_name} · {r.display_id}</div>
                    <div className="text-[13px] text-ink-2">
                      {t(`requests.deliverable_n_${r.deliverable_type.toLowerCase()}`, { count: r.quantity })}
                      {r.preferred_format ? ` · ${r.preferred_format}` : ""} · {t(`plan.method_${(r.production_method ?? "ai").toLowerCase()}`)} · {t("plan.version_n", { n: r.plan_version })}
                    </div>
                  </div>
                  {r.deadline && <span className="text-[13px] text-ink-2">{t("requests.due_on", { date: formatDate(r.deadline, i18n.language, me.time_zone) })}</span>}
                  <span className="rounded-full bg-[var(--accent-secondary-soft)] px-3 py-1 text-[12px] font-semibold text-magenta-text">{t("honest.awaiting_ai_production_phase")}</span>
                </Link>
              ))}
            </Card>
          )}
        </QueryState>
      </section>
    </div>
  );
}
